/**
 * Button glyphs for the hints bar (`03-client-app.md` section 3.4).
 *
 * Xbox is the default. PlayStation is picked up from the controller's vendor id, and the Steam
 * Deck set is used when the native layer reports running on one. A user can override the choice
 * in settings, which is what `glyphOverride` is for.
 */

import type { ControllerType, GamepadButton } from './input.js';

export type GlyphSet = 'xbox' | 'playstation' | 'steamdeck';

const GLYPHS: Record<GlyphSet, Partial<Record<GamepadButton, string>>> = {
  xbox: {
    a: 'A',
    b: 'B',
    x: 'X',
    y: 'Y',
    lb: 'LB',
    rb: 'RB',
    lt: 'LT',
    rt: 'RT',
    start: 'Menu',
    select: 'View',
    up: '↑',
    down: '↓',
    left: '←',
    right: '→',
  },
  playstation: {
    a: '✕',
    b: '○',
    x: '□',
    y: '△',
    lb: 'L1',
    rb: 'R1',
    lt: 'L2',
    rt: 'R2',
    start: 'Options',
    select: 'Create',
    up: '↑',
    down: '↓',
    left: '←',
    right: '→',
  },
  steamdeck: {
    a: 'A',
    b: 'B',
    x: 'X',
    y: 'Y',
    lb: 'L1',
    rb: 'R1',
    lt: 'L2',
    rt: 'R2',
    start: '☰',
    select: '⧉',
    up: '↑',
    down: '↓',
    left: '←',
    right: '→',
  },
};

export function glyphSetFor(controller: ControllerType, override?: GlyphSet | 'auto'): GlyphSet {
  if (override && override !== 'auto') return override;
  switch (controller) {
    case 'playstation':
      return 'playstation';
    case 'steamdeck':
      return 'steamdeck';
    default:
      return 'xbox';
  }
}

export function glyph(button: GamepadButton, set: GlyphSet): string {
  return GLYPHS[set][button] ?? button.toUpperCase();
}

/** What each button does, as a default the hints bar falls back to. */
export const DEFAULT_ACTIONS: Partial<Record<GamepadButton, string>> = {
  a: 'hints.select',
  b: 'hints.back',
  lb: 'hints.prevTab',
  rb: 'hints.nextTab',
  lt: 'hints.pageUp',
  rt: 'hints.pageDown',
  start: 'hints.menu',
  select: 'hints.notifications',
};
